import { Button, Table, Tag } from 'antd'
import { observer } from 'mobx-react-lite'

import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { classNames } from '@/Ship/Helpers/classNames'

import { CLIENT_CARD_RESOURCE, CLIENT_LOSSES_NO_REASON } from '../Contracts/IClientLossesReport'
import { formatReportCount } from '../Helpers/reportFormat'
import { NUMERIC_SORT_DIRECTIONS } from '../Helpers/reportSort'
import { clientLossesReportStore as store } from '../Stores/ReportStore'

import type { IClientLossesReport, IClientLossesRow } from '../Contracts/IClientLossesReport'
import type { TableColumnsType } from 'antd'

import styles from './Report.module.css'

const countOf = (row: IClientLossesRow, key: string): number => row.byReason[key] ?? 0

/**
 * Таблиця «Звіт по програним заявкам» (`SPEC-027` §4.4): клієнт, «Заявки»,
 * колонка на кожну причину («Причина: <назва>», як у легасі), видалена
 * причина — з позначкою (Q3.4), «Без причини» — останньою і лише коли
 * в ній не нуль (ЗВ-05: сума колонок дорівнює «Заявки»). «Разом» — з бека (П-07).
 *
 * Параметра сортування бек не має (порядок — «Заявки» спадно, П-04), тож
 * колонки сортує сама таблиця: дані невеликі, «Разом» стоїть окремо й не
 * пересортовується. Клік по клієнту відкриває його картку за ключем ресурсу
 * (П-05) — без імпорту з `CrmSection`.
 */
export const ClientLossesTable = observer(({ report }: { report: IClientLossesReport }) => {
    const reasonColumns = [
        ...report.reasons.map((reason) => ({
            key: reason.uuid,
            title: (
                <span>
                    Причина: {reason.title}
                    {reason.isRemoved ? (
                        <>
                            {' '}
                            <Tag className={classNames(styles['mark'])}>видалена</Tag>
                        </>
                    ) : null}
                </span>
            ),
        })),
        // Причина програшу обов'язкова, тож «Без причини» на живих даних майже
        // завжди нуль — колонку показуємо, лише коли за період є такі програші
        // (рішення замовника 2026-10-22). Сума колонок і далі дорівнює «Заявки».
        ...((report.total.byReason[CLIENT_LOSSES_NO_REASON] ?? 0) > 0
            ? [{ key: CLIENT_LOSSES_NO_REASON, title: <span>Без причини</span> }]
            : []),
    ]

    const columns: TableColumnsType<IClientLossesRow> = [
        {
            key: 'client',
            title: 'Клієнт',
            sorter: (a, b) => a.client.title.localeCompare(b.client.title, 'uk'),
            render: (_: unknown, row) => (
                <Button
                    type="link"
                    style={{ padding: 0, height: 'auto', whiteSpace: 'normal', textAlign: 'left' }}
                    onClick={() => OpenResourceCardTask({ resource: CLIENT_CARD_RESOURCE, primaryFieldValue: row.client.uuid })}
                >
                    {row.client.title}
                </Button>
            ),
        },
        {
            key: 'applications',
            title: 'Заявки',
            align: 'right',
            sorter: (a, b) => a.applications - b.applications,
            sortDirections: NUMERIC_SORT_DIRECTIONS,
            render: (_: unknown, row) => formatReportCount(row.applications),
        },
        ...reasonColumns.map(({ key, title }) => ({
            key,
            title,
            align: 'right' as const,
            sorter: (a: IClientLossesRow, b: IClientLossesRow) => countOf(a, key) - countOf(b, key),
            sortDirections: NUMERIC_SORT_DIRECTIONS,
            render: (_: unknown, row: IClientLossesRow) => formatReportCount(countOf(row, key)),
        })),
    ]

    return (
        <Table<IClientLossesRow>
            data-testid="report-losses-table"
            rowKey={(row) => row.client.uuid}
            columns={columns}
            dataSource={report.rows}
            pagination={false}
            loading={store.isLoading}
            scroll={{ x: 'max-content' }}
            showSorterTooltip={false}
            summary={() => (
                <Table.Summary fixed>
                    <Table.Summary.Row className={classNames(styles['totalRow'])} data-testid="report-total">
                        <Table.Summary.Cell index={0}>Разом</Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                            {formatReportCount(report.total.applications)}
                        </Table.Summary.Cell>
                        {reasonColumns.map(({ key }, index) => (
                            <Table.Summary.Cell key={key} index={index + 2} align="right">
                                {formatReportCount(report.total.byReason[key] ?? 0)}
                            </Table.Summary.Cell>
                        ))}
                    </Table.Summary.Row>
                </Table.Summary>
            )}
        />
    )
})
